import {computed, observable, runInAction, action} from 'mobx';
import clone from 'lodash/clone';
import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import map from 'lodash/map';
import orderBy from 'lodash/orderBy';
import {ICountryInfo} from '@techsee/techsee-ui-common/lib/_contracts/CountryInfo';
import {
    InviteMethodSettings,
    VisualJourneyItem,
    ClientLanguageItem,
    ReferralRegionItem,
    VirtualBackgroundItem,
    InitialModeItem
} from '../../../../../models/AccountSettings';
import {InviteMethodForm} from './form-model';
import {ISmsService, ValidationResultCodes} from '../../../../../services/SmsService';
import {IFieldFactory} from '../../../../../app.contracts';
import {OperationResult} from '../../../../../models/OperationResult';
import {IInviteFlowManager} from '../../../_contracts/InviteContracts';
import {InviteMethodInfo} from '../../../../../models/LiveSessionState';
import {
    MeetingMode,
    AddressTypesEnum,
    NativeFullAssistType
} from '@techsee/techsee-common/lib/constants/room.constants';
import {ITsEnvironmentDetect} from '@techsee/techsee-common/lib/helpers/ts-environment-detect';
import {
    emailSendBy,
    MediaTypes,
    ReferralConfigurations,
    VideoFilterType
} from '@techsee/techsee-common/lib/constants/account.constants';
import {getRootStore} from '../../../../../app.bootstrap';
import {calculatePhoneNumberValue} from '@techsee/techsee-ui-common/lib/forms/phone-number-field';
import {ISelectVideoPublisherController} from '../../../../../components/select-video-publisher/controller';
import {Nullable} from '@techsee/techsee-common';
import {DeviceCategory} from '@techsee/techsee-common/lib/constants/utils.constant';
import {countryCodes} from '@techsee/techsee-country-codes/lib/index';

// @ts-ignore
import {sanitizeCountryCode} from '@techsee/techsee-common/lib/utils';
import {IBrowserUtilsService} from '@techsee/techsee-client-infra/lib/services/BrowserUtilsService';
import {
    AdditionalSessionType,
    InternalSessionType,
    MeetingModes
} from '../../../../../components/start-with-modes/controller';

export interface IInviteMethodController {
    readonly inviteForm: InviteMethodForm;

    readonly isVideoApplicationEnabled: boolean;
    readonly validateNumberInProgress: boolean;
    readonly creatingSessionInProgress: boolean;
    readonly isCustomerRefEnabled: boolean;
    readonly isCoBrowsingEnabled: boolean;
    readonly isEnableNativeFullAssist: boolean;
    readonly isEnableAgentWifiScan: boolean;
    readonly isPreDataCollectionCodeEnabled: boolean;
    readonly isPhoneNumberEnabled: boolean;
    readonly isStartWithModesEnabled: boolean;
    readonly isCountryCodeEnabled: boolean;
    readonly isLanguageSelectionEnabled: boolean;
    readonly getInitialNativeModes: InitialModeItem[];
    readonly showSelectVideoPublisher: boolean;
    readonly phoneCountryCodes: string[];
    readonly defaultCountryCode: string | number;
    _selectVideoPublisherController: Nullable<ISelectVideoPublisherController>;

    readonly countriesInfo: ICountryInfo[];
    readonly countryList: ICountryInfo[];
    readonly clientLanguages: ClientLanguageItem[];
    readonly initialNativeModes: InitialModeItem[];
    readonly initialNativeMode?: string;
    readonly visualJourniesList: VisualJourneyItem[];
    readonly referralRegionList: ReferralRegionItem[];
    readonly virtualBackgroundList: VirtualBackgroundItem[];
    readonly isVirtualBackgroundEnabled: boolean;
    readonly isVisualJourneySelected: boolean;
    readonly isVideoApplicationSelected: boolean;
    readonly isNativeFullAssistModeActive: boolean;
    readonly isFaceMeetSelected: boolean;
    readonly visualJourneyName: string;
    readonly isInviteByEmail: boolean;
    readonly initialNativeModeName?: string;
    readonly isDesktopSharing?: boolean;
    enableAudio: boolean;
    enableVideo?: boolean;
    displayStartWithAudio: boolean;
    displayStartWithVideo: boolean;
    isAudioSupportedMode: boolean;
    readonly isCobrowsingSelectedMode: boolean;
    isVideoSupportedMode: boolean;
    addressType: AddressTypesEnum;
    deviceCategory: DeviceCategory | null;
    allowSendByWhatsApp: boolean;
    isNativeFullAssistSelected: boolean;

    readonly isSessionTagsEnabled: boolean;
    readonly availableSessionTags: string[];
    readonly selectedSessionTags: ReadonlyArray<string>;
    readonly maxSessionTags: number;

    toggleSessionTag(tag: string): void;

    removeSessionTag(tag: string): void;

    audioForRoomToggle(): void;

    videoForRoomToggle(): void;

    validateInviteForm(): PromiseLike<OperationResult>;

    resetInviteForm(): void;

    rebuildForm(addressType: AddressTypesEnum): void;
    setInitialNativeMode(val: string): void;

    updateInviteState(): Promise<void>;

    resetInviteState(): void;

    setCreatingSessionInProgress(inProgress: boolean): void;

    setPhoneNumber(value: string): void;

    setCountryCode(value: string): void;

    setCustomerRef(value: string): void;

    setEmailAddress(value: string): void;
}

export class InviteMethodController implements IInviteMethodController {
    private _smsService: ISmsService;

    private _flowManager: IInviteFlowManager;

    private liveSdk: {enableNativeFullAssist?: boolean; enabled?: boolean};

    private _accountSettings: InviteMethodSettings;

    private _environmentDetect: ITsEnvironmentDetect;

    private _audioConfig: any;

    private _deviceCategory: Nullable<DeviceCategory>;

    private _browserUtilsService: IBrowserUtilsService;

    private _resolvePreDataCollectionWarning?: (proceeded: boolean) => void;

    readonly inviteForm: InviteMethodForm;

    allowSendByWhatsApp: boolean;
    initialNativeModes: InitialModeItem[];
    initialNativeMode?: string;

    @observable
    initialNativeModeName?: string;

    _selectVideoPublisherController: Nullable<ISelectVideoPublisherController>;

    @observable private _validateNumberInProgress: boolean = false;

    @observable private _creatingSessionInProgress: boolean = false;

    @observable
    private _countriesInfoList: ICountryInfo[] = [];

    @observable countryList: ICountryInfo[] = [];

    private _pendingEmail: string = '';

    @observable _enableAudio: boolean;

    @observable _enableVideo?: boolean;

    @observable private _selectedSessionTags: string[] = [];

    private _displayStartWithAudio: boolean;

    private _displayStartWithVideo: boolean;

    isDesktopSharing?: boolean;

    constructor(
        flowManager: IInviteFlowManager,
        accSettings: InviteMethodSettings,
        smsService: ISmsService,
        fieldFactory: IFieldFactory,
        browserUtilsService: IBrowserUtilsService,
        isDesktopSharing?: boolean
    ) {
        this._smsService = smsService;
        this.liveSdk = accSettings.liveSdk;
        this.isDesktopSharing = isDesktopSharing;
        this._flowManager = flowManager;
        this._accountSettings = accSettings;
        this._environmentDetect = getRootStore().environmentService;
        this._browserUtilsService = browserUtilsService;
        this.countryList = countryCodes;
        this.initialNativeModes = [];
        this.updateInitialMode();
        this._deviceCategory = this._environmentDetect.deviceCategory(getRootStore().displayTabletAsDesktop);
        this._audioConfig = this.getAudioConfig();
        this._displayStartWithAudio = this.shouldAllowAudio() && this._audioConfig && this._audioConfig.enableAudio;
        this._displayStartWithVideo = this.shouldAllowVideo();
        this._enableAudio = this._displayStartWithAudio && this._audioConfig.startSessionWithAudio;
        this._enableVideo = get(this._accountSettings, 'v2_1.enabled')
            ? this._accountSettings.agentStartSessionWithVideo && this._displayStartWithVideo
            : undefined;
        this.inviteForm = new InviteMethodForm(accSettings, this._flowManager.translate, fieldFactory);
        this.allowSendByWhatsApp = accSettings.allowSendByWhatsApp;
        this._selectVideoPublisherController = this._flowManager._selectVideoPublisherController;
        this.createFormFields(this.inviteForm);
        this.audioForRoomToggle = this.audioForRoomToggle.bind(this);
        this.videoForRoomToggle = this.videoForRoomToggle.bind(this);

        if (this.isCountryCodeEnabled && this.isPhoneNumberEnabled) {
            Promise.resolve(this.countryList).then((countryList) => {
                runInAction(() => {
                    this._countriesInfoList = countryList.filter(
                        (country) =>
                            accSettings.phoneCountryCodes.indexOf(country.code) > -1 ||
                            `+${accSettings.defaultCountryCode}` === country.dial
                    );

                    const countryCodeValueFromParams = this._browserUtilsService.getParamValue('c');

                    this.initializeAccountCountryCode(countryCodeValueFromParams);
                });
            });
        }
    }

    get isPhoneNumberEnabled(): boolean {
        return (
            this.isInviteBySMS ||
            this.isInviteByWhatsApp ||
            (this.isInviteByCode && this._accountSettings.disablePhoneNumberForInviteByCode)
        );
    }

    get showSelectVideoPublisher(): boolean {
        const env = this.deviceCategory!;
        const currentMode = this._flowManager.startWithModesController?.currentMode?.mode;
        const isCurrentModeSupported = includes(
            [MeetingMode.video, MeetingMode.images, MeetingMode.oneClick],
            currentMode
        );

        return (this._accountSettings.selectVideoPublisher as any)[env] && isCurrentModeSupported;
    }

    get isVideoApplicationEnabled(): boolean {
        return !!find(this._accountSettings.startWithModes.modesOptions, {mode: MeetingMode.videoApplication});
    }

    get isVirtualBackgroundEnabled(): boolean {
        return (
            this._accountSettings.virtualBackground.enabledBlurAgent ||
            this._accountSettings.virtualBackground.enabledImageReplacementAgent
        );
    }

    get isCustomerRefEnabled(): boolean {
        return (
            (this.isInviteByCode && this._accountSettings.customerIdValidationRules.displayForInviteByCode) ||
            (this.isInviteBySMS && this._accountSettings.customerIdValidationRules.display) ||
            (this.isInviteByWhatsApp && this._accountSettings.customerIdValidationRules.display) ||
            (this.isInviteByEmail && this._accountSettings.customerIdValidationRules.displayForInviteByEmail)
        );
    }

    get isEnableNativeFullAssist(): boolean {
        return !!(this.liveSdk?.enableNativeFullAssist && this.liveSdk.enabled);
    }
    get isEnableAgentWifiScan(): boolean {
        return !!this._accountSettings.enableAgentWifiScan;
    }

    @computed
    get isPreDataCollectionCodeEnabled(): boolean {
        return this.isEnableAgentWifiScan && this.isEnableNativeFullAssist && this.isNativeFullAssistModeActive;
    }

    get isCoBrowsingEnabled(): boolean {
        return this._accountSettings.coBrowsing.enabled;
    }

    get isInviteByEmailEnabled(): boolean {
        return (
            this._accountSettings.emailSendBy === emailSendBy.tsEmailService &&
            (this._accountSettings.allowSendByEmail || this._accountSettings.disableEmailForInviteByCode)
        );
    }

    get isInviteByEmail(): boolean {
        return this.addressType === AddressTypesEnum.EMAIL && this.isInviteByEmailEnabled;
    }

    get addressType(): AddressTypesEnum {
        return this._flowManager.inviteState.inviteMethodInfo.addressType;
    }

    get isInviteByCode(): boolean {
        return this.addressType === AddressTypesEnum.INVITE_BY_CODE;
    }

    get isInviteBySMS(): boolean {
        return this.addressType === AddressTypesEnum.SMS;
    }

    get isInviteByWhatsApp(): boolean {
        return this.addressType === AddressTypesEnum.WHATSAPP;
    }

    get isStartWithModesEnabled(): boolean {
        return this._accountSettings.startWithModes.enabled;
    }

    get isCountryCodeEnabled(): boolean {
        if (this.isInviteByWhatsApp) {
            // When WhatsApp has its own country-code config enabled, use it.
            // Otherwise fall back to the SMS behavior for backward compatibility (per HY-669 AC).
            return this._accountSettings.whatsapp.addCountryCode || this._accountSettings.addCountryCodeToSms;
        }
        return this._accountSettings.addCountryCodeToSms;
    }

    get getInitialNativeModes(): InitialModeItem[] {
        return this.initialNativeModes;
    }

    get isLanguageSelectionEnabled(): boolean {
        return this._accountSettings.multiLanguage && !isEmpty(this.clientLanguages);
    }

    get phoneCountryCodes(): string[] {
        if (this.isInviteByWhatsApp && this._accountSettings.whatsapp.addCountryCode) {
            return this._accountSettings.whatsapp.selectedCountries;
        }
        return this._accountSettings.phoneCountryCodes;
    }

    get defaultCountryCode(): string | number {
        return this._accountSettings.defaultCountryCode;
    }

    @computed
    get countriesInfo(): ICountryInfo[] {
        const activeCodes = this.phoneCountryCodes || [];
        const defaultDial = `+${this._accountSettings.defaultCountryCode}`;
        return this.countryList.filter(
            (country) => activeCodes.indexOf(country.code) > -1 || defaultDial === country.dial
        );
    }

    @computed
    get validateNumberInProgress(): boolean {
        return this._validateNumberInProgress;
    }

    @computed
    get getInitialNativeModeName(): string | undefined {
        return this.initialNativeModeName;
    }

    @computed
    get creatingSessionInProgress(): boolean {
        return this._creatingSessionInProgress;
    }

    @action
    setCreatingSessionInProgress(inProgress: boolean) {
        this._creatingSessionInProgress = inProgress;
    }

    @computed
    get clientLanguages(): ClientLanguageItem[] {
        return orderBy(
            map(this._accountSettings.clientLanguages, (clientLanguage: Record<string, any>) => {
                const item: ClientLanguageItem = {
                    name: clientLanguage.name,
                    value: clientLanguage.value
                };

                return item;
            }),
            ['name'],
            ['asc']
        );
    }

    @computed
    get selectedClientLanguage(): string {
        const languageName = get(this.inviteForm, `fields.${this.inviteForm.fieldNames.selectedLanguage}.value`, '');

        if (!languageName) {
            return '';
        }

        try {
            return this.clientLanguages.find((clientLanguage) => clientLanguage.name === languageName)!.value;
        } catch (error) {
            return '';
        }
    }

    @computed
    get selectedVirtualBackground(): VideoFilterType {
        const virtualBackground = get(
            this.inviteForm,
            `fields.${this.inviteForm.fieldNames.virtualBackground}.value`,
            VideoFilterType.NONE
        );

        if (!virtualBackground) {
            return this._accountSettings.virtualBackground.default;
        }

        return (
            (this.virtualBackgroundList.find((item) => item.name === virtualBackground)?.value as VideoFilterType) ||
            VideoFilterType.NONE
        );
    }

    @computed
    get visualJourniesList(): VisualJourneyItem[] {
        return this._accountSettings.visualJourney.available;
    }

    @computed
    get virtualBackgroundList(): VirtualBackgroundItem[] {
        const {enabledBlurAgent, enabledImageReplacementAgent} = this._accountSettings.virtualBackground;
        const enabledOptions: VirtualBackgroundItem[] = [
            {
                value: VideoFilterType.NONE,
                name: this._flowManager.translate('REACT.INVITE.VIEW.SELECT_OPTION_NONE')
            }
        ];

        if (enabledBlurAgent) {
            enabledOptions.push({
                value: VideoFilterType.BLUR,
                name: this._flowManager.translate('REACT.INVITE.VIEW.SELECT_OPTION_BLUR')
            });
        }
        if (enabledImageReplacementAgent) {
            enabledOptions.push({
                value: VideoFilterType.BACKGROUND,
                name: this._flowManager.translate('REACT.INVITE.VIEW.SELECT_OPTION_VIDEO_BACKGROUND')
            });
        }

        return enabledOptions;
    }

    @computed
    get isVisualJourneySelected(): boolean {
        return this._flowManager.startWithModesController?.currentMode?.mode === MeetingMode.visualJourney;
    }

    @computed
    get isFaceMeetSelected(): boolean {
        return this._flowManager.startWithModesController?.currentMode?.mode === MeetingMode.faceMeet;
    }

    @computed
    get referralRegionList(): ReferralRegionItem[] {
        return map(ReferralConfigurations, (key, value) => ({name: key, value}));
    }

    @computed
    get selectedReferralRegion(): string {
        const referralRegion = get(this.inviteForm, `fields.${this.inviteForm.fieldNames.referralRegion}.value`, '');

        if (!referralRegion) {
            return this._accountSettings.referralConfiguration;
        }

        try {
            return (find(this.referralRegionList, {name: referralRegion}) as ReferralRegionItem).value;
        } catch (error) {
            return '';
        }
    }

    @computed
    get isVideoApplicationSelected(): boolean {
        return this._flowManager.startWithModesController?.currentMode?.mode === MeetingMode.videoApplication;
    }

    @computed
    get isNativeFullAssistModeActive(): boolean {
        return this._flowManager.startWithModesController?.currentMode?.mode === AdditionalSessionType.nativeFullAssist;
    }

    private getAudioConfig() {
        const env = this.deviceCategory;

        return env && this._accountSettings.audioStartMode && (this._accountSettings.audioStartMode as any)[env];
    }

    private shouldAllowAudio() {
        return (
            this._accountSettings.enableSessionToolbar && this._accountSettings.mediaType === MediaTypes.videoAndAudio
        );
    }

    private shouldAllowVideo() {
        return (
            this._accountSettings.enableSessionToolbar &&
            (this._accountSettings.mediaType === MediaTypes.videoAndAudio ||
                this._accountSettings.mediaType === MediaTypes.video)
        );
    }

    get deviceCategory() {
        return this._deviceCategory;
    }

    @computed
    get isAudioSupportedMode() {
        return (
            this._flowManager.startWithModesController?.currentMode?.mode === MeetingMode.video ||
            this._flowManager.startWithModesController?.currentMode?.mode === MeetingMode.faceMeet ||
            this._flowManager.startWithModesController?.currentMode?.mode === MeetingMode.videoApplication ||
            get(this._accountSettings, 'v3.enabled')
        );
    }

    @computed
    get isCobrowsingSelectedMode() {
        return this._flowManager.startWithModesController?.currentMode?.mode === MeetingMode.coBrowsing;
    }

    @computed
    get isNativeFullAssistSelected() {
        const isNative =
            this._flowManager.startWithModesController?.currentMode?.mode === AdditionalSessionType.nativeFullAssist ||
            this._flowManager.inviteState.sessionInfo.isWifiScanActive;

        return isNative && this.isEnableAgentWifiScan;
    }

    @computed
    get isVideoSupportedMode() {
        return (
            this._flowManager.startWithModesController?.currentMode?.mode === MeetingMode.faceMeet &&
            get(this._accountSettings, 'v2_1.enabled')
        );
    }

    @computed
    get shouldStartWithAudio() {
        return this._enableAudio && this.isAudioSupportedMode;
    }

    @computed
    get isVisualJourneyEnabled(): boolean {
        return this._accountSettings.visualJourney.enabled;
    }

    @computed
    get referralRegion(): boolean {
        return get(
            this.inviteForm,
            `fields.${this.inviteForm.fieldNames.referralRegion}.value`,
            ''
        ) as unknown as boolean;
    }

    @computed
    get visualJourneyName(): string {
        return get(this.inviteForm, `fields.${this.inviteForm.fieldNames.visualJourney}.value`, '') as string;
    }

    @computed
    get visualJourneyAlias(): string {
        const vjName = get(this.inviteForm, `fields.${this.inviteForm.fieldNames.visualJourney}.value`);

        try {
            return this.visualJourniesList.find((journey) => journey.name === vjName)!.alias;
        } catch (error) {
            return '';
        }
    }

    @computed
    get enableAudio() {
        return this._enableAudio;
    }

    @computed
    get enableVideo() {
        return this._enableVideo;
    }

    get displayStartWithAudio() {
        return this._displayStartWithAudio;
    }

    get displayStartWithVideo() {
        return this._displayStartWithVideo;
    }

    get maxSessionTags(): number {
        return this._accountSettings.maxSessionTags;
    }

    @computed
    get availableSessionTags(): string[] {
        return this._accountSettings.sessionTags || [];
    }

    @computed
    get isSessionTagsEnabled(): boolean {
        return Boolean(this._accountSettings.allowAddSessionTags) && this.availableSessionTags.length > 0;
    }

    @computed
    get selectedSessionTags(): ReadonlyArray<string> {
        return this._selectedSessionTags;
    }

    @action
    toggleSessionTag(tag: string): void {
        const index = this._selectedSessionTags.indexOf(tag);

        if (index > -1) {
            this._selectedSessionTags.splice(index, 1);
        } else if (this._selectedSessionTags.length < this.maxSessionTags) {
            this._selectedSessionTags.push(tag);
        }
    }

    @action
    removeSessionTag(tag: string): void {
        const index = this._selectedSessionTags.indexOf(tag);

        if (index > -1) {
            this._selectedSessionTags.splice(index, 1);
        }
    }

    @action
    audioForRoomToggle() {
        this._enableAudio = !this._enableAudio;
    }

    @action
    videoForRoomToggle() {
        this._enableVideo = !this._enableVideo;
    }

    updateInitialModes(item: InitialModeItem): void {
        if (!this.initialNativeModeName) {
            this.initialNativeModeName = item.name;
            this.initialNativeMode = item.value;
        }
    }

    updateInitialMode(): void {
        const options = this._accountSettings.startWithModes.modesOptions;

        this.initialNativeModes = [];

        for (const option of options) {
            const {mode, priority} = option;

            let item: InitialModeItem | null = null;

            if (this.isEnableAgentWifiScan && this.isEnableNativeFullAssist) {
                if (mode === MeetingMode.videoApplication) {
                    item = {name: MeetingMode.videoApplication, value: NativeFullAssistType.VIDEO};
                    this.updateInitialModes(item);
                }

                if (mode === MeetingMode.screen && !this.isDesktopSharing) {
                    item = {name: MeetingMode.screen, value: NativeFullAssistType.INSTANT_MIRRORING};
                    this.updateInitialModes(item);
                }

                if (item) {
                    this.initialNativeModes.push(item);
                    if (priority === 'primary') {
                        this.initialNativeMode = item.value;
                        this.initialNativeModeName = item.name;
                    }
                }
            }
        }
    }

    async validateInviteForm(): Promise<OperationResult> {
        const isFormValid = await this.inviteForm.validateForm();

        if (!isFormValid) {
            this.inviteForm.displayFieldsErrors();

            return OperationResult.getFailResult();
        }

        const preDataCollectionPassed = await this._validatePreDataCollectionCode();

        if (!preDataCollectionPassed) {
            return OperationResult.getFailResult();
        }

        if (this.isPhoneNumberEnabled) {
            return this._validatePhoneNumber();
        }

        return OperationResult.getSuccessResult();
    }

    private _validatePhoneNumber(): Promise<OperationResult> {
        return new Promise<OperationResult>((resolve) => {
            const changePhoneValidationStatus = (isValidating: boolean) => {
                runInAction(() => (this._validateNumberInProgress = isValidating));
            };

            changePhoneValidationStatus(true);
            const {address, countryCode, phoneNumber} = this.setAddressDetails();

            Promise.resolve(this._smsService.validatePhoneNumber(countryCode, phoneNumber))
                .then((result: OperationResult<{lookupPhoneNumber?: string}>) => {
                    changePhoneValidationStatus(false);

                    if (!result.isSuccess) {
                        const lookupPhoneNumber = result.data && result.data.lookupPhoneNumber;

                        if (result.message === ValidationResultCodes.INVALIDNUMBER && lookupPhoneNumber) {
                            const validatedPhone = `+${lookupPhoneNumber}`;

                            this._flowManager.lookupNumberController
                                .showLookupModal({
                                    enteredPhone: address,
                                    validatedPhone: validatedPhone,
                                    isFromChangeNumber:
                                        this._flowManager.changeNumberController.isDisplayConfirmationModal,
                                    logData: {
                                        accountId: this._flowManager.appState.accountId,
                                        userId: this._flowManager.appState.appUser!._id,
                                        roomId: this._flowManager.inviteState.sessionInfo.sessionRoomId
                                    }
                                })
                                .then(() => {
                                    const codeWithoutPlus = this.inviteForm.fields.countryCode.value
                                        .toString()
                                        .replace('+', '');
                                    const newPhoneNumber = lookupPhoneNumber.replace(codeWithoutPlus, '');

                                    this.inviteForm.fields.phoneNumber.setValue(newPhoneNumber);

                                    resolve(OperationResult.getSuccessResult());
                                })
                                .catch(() => {
                                    resolve(OperationResult.getSuccessResult());
                                });
                        } else {
                            //TODO: Check where I take carrier name from??
                            const errMsg = this._flowManager.translate(
                                'REACT.INVITE.VIEW.PHONE_NUM.ERROR.' + result.message,
                                {phonenumber: phoneNumber}
                            );

                            this.inviteForm.setFieldError('address', errMsg);
                            resolve(OperationResult.getFailResult());
                        }
                    } else {
                        resolve(OperationResult.getSuccessResult());
                    }
                })
                .catch(() => {
                    changePhoneValidationStatus(false);
                    resolve(OperationResult.getFailResult());
                });
        });
    }

    private async _validatePreDataCollectionCode(): Promise<boolean> {
        if (!this.isPreDataCollectionCodeEnabled) {
            return true;
        }

        const preDataCollectionCodeField = this.inviteForm.fields[this.inviteForm.fieldNames.preDataCollectionCodeRef];
        const code = (preDataCollectionCodeField?.value as string)?.trim() ?? '';

        if (!code) {
            return true;
        }

        try {
            const result = await this._flowManager.validatePreDataCollectionCode(code);

            if (result.valid) {
                return true;
            }
        } catch {
            const errMsg = this._flowManager.translate('REACT.INVITE.VIEW.PRE_DATA_COLLECTION.VALIDATION_ERROR');

            this.inviteForm.setFieldError(this.inviteForm.fieldNames.preDataCollectionCodeRef, errMsg);

            return false;
        }

        return this._showPreDataCollectionWarningModal();
    }

    private _initPreDataCollectionWarningModal(): void {
        const modal = this._flowManager.inviteWizardController.preDataCollectionWarningModal;

        modal.onPositiveClick = () => {
            modal.hide();
            this._resolvePreDataCollectionWarning?.(true);
        };

        modal.onNegativeClick = () => {
            modal.hide();
            this._resolvePreDataCollectionWarning?.(false);
        };
    }

    @action
    private _showPreDataCollectionWarningModal(): Promise<boolean> {
        this._initPreDataCollectionWarningModal();

        return new Promise<boolean>((resolve) => {
            this._resolvePreDataCollectionWarning = resolve;
            this._flowManager.inviteWizardController.preDataCollectionWarningModal.show();
        });
    }

    updateInviteState(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.validateInviteForm().then((validationResult) => {
                if (validationResult.isSuccess) {
                    const prevFormValues = clone(this._flowManager.inviteState.inviteMethodInfo);
                    const currentFormValues = this.inviteForm.getFormValues();

                    currentFormValues.addressType = this._flowManager.inviteState.inviteMethodInfo.addressType;

                    currentFormValues.visualJourneyAlias = this.visualJourneyAlias;

                    currentFormValues.clientLanguage = this.selectedClientLanguage;
                    currentFormValues.enableAudio = this.shouldStartWithAudio;
                    currentFormValues.enableVideo = this.isVideoSupportedMode ? this._enableVideo : undefined;
                    currentFormValues.virtualBackground = this.selectedVirtualBackground;
                    currentFormValues.sessionTags = this.isSessionTagsEnabled ? [...this._selectedSessionTags] : [];

                    const preDataCollectionCodeField =
                        this.inviteForm.fields[this.inviteForm.fieldNames.preDataCollectionCodeRef];
                    if (preDataCollectionCodeField) {
                        currentFormValues.preDataCollectionCodeRef = (preDataCollectionCodeField.value as string) ?? '';
                    }

                    this._flowManager.updateInviteMethod(currentFormValues);

                    const newFormValues = clone(this._flowManager.inviteState.inviteMethodInfo);

                    this._flowManager.inviteLogger.inviteMethodChangedByModal(prevFormValues, newFormValues);

                    currentFormValues.referralRegion = this.selectedReferralRegion;

                    resolve();
                } else {
                    reject({message: 'Invite method form is in invalid state', formError: true});
                }
            });
        });
    }

    resetInviteState(): void {
        this._flowManager.updateInviteMethod(new InviteMethodInfo());

        if (this.isLanguageSelectionEnabled) {
            const {inviteMethodInfo} = this._flowManager.inviteState;
            const {clientLanguage} = inviteMethodInfo;

            if (clientLanguage) {
                const clientLanguageToSelect =
                    clientLanguage && this.clientLanguages.find((language) => language.value === clientLanguage);

                if (clientLanguageToSelect) {
                    this.inviteForm.fields[this.inviteForm.fieldNames.selectedLanguage].setValue(
                        clientLanguageToSelect.name
                    );
                }
            }
        }

        // set the selected app localization
        if (this.isVideoApplicationEnabled && (this.isVideoApplicationSelected || this.isNativeFullAssistSelected)) {
            const referralConfiguration = find(this.referralRegionList, {
                value: this._accountSettings.referralConfiguration
            });

            this.inviteForm.fields[this.inviteForm.fieldNames.referralRegion].setValue(referralConfiguration!.name);
        }
    }

    resetInviteForm(): void {
        this.rebuildFormInternal();

        if (this.isCountryCodeEnabled && this.isPhoneNumberEnabled) {
            this.initializeAccountCountryCode();
        }
    }
    @action
    setInitialNativeMode(mode: string): void {
        this.initialNativeModeName = mode;
    }

    rebuildForm(addressType: AddressTypesEnum): void {
        const currentFormValues = this.inviteForm.getFormValues();

        currentFormValues.addressType = addressType;

        this._flowManager.updateInviteMethod(currentFormValues);
        this.rebuildFormInternal();
    }

    @action
    private rebuildFormInternal(): void {
        this.inviteForm.removeFields();
        this.createFormFields(this.inviteForm);
    }

    @action
    setPhoneNumber(value: string): void {
        const {phoneNumber} = this.inviteForm.fieldNames;

        this.inviteForm.fields[phoneNumber].setValue(value);
    }

    @action
    setCountryCode(value: string): void {
        const countryCodeValue = sanitizeCountryCode(value);
        const {countryCode} = this.inviteForm.fieldNames;

        this.inviteForm.fields[countryCode].setValue(`+${countryCodeValue}`);
    }

    @action
    setCustomerRef(value: string): void {
        const {customerRef} = this.inviteForm.fieldNames;

        this.inviteForm.fields[customerRef].setValue(value);
    }

    @action
    setEmailAddress(value: string): void {
        this._pendingEmail = value;
    }

    private createFormFields(inviteForm: InviteMethodForm): void {
        const {inviteMethodInfo} = this._flowManager.inviteState;
        const {
            customerRef,
            selectedLanguage,
            phoneNumber,
            countryCode,
            coBrowsingInitialUrl,
            initialNativeMode,
            preDataCollectionCodeRef
        } = this.inviteForm.fieldNames;

        const isActiveSession = this._flowManager.inviteState.sessionInfo.isSessionActive;

        if (!isActiveSession) {
            this._flowManager.resetAddressField();
        }

        if (this.isPhoneNumberEnabled && !this.isInviteByEmail) {
            inviteForm.createAddressField(this.isCountryCodeEnabled, this.addressType);
            inviteForm.fields[phoneNumber].setValue(inviteMethodInfo.phoneNumber || '');
        }

        if (this.isInviteByEmail) {
            inviteForm.createAddressField(this.isCountryCodeEnabled, this.addressType);
            const emailValue = this._pendingEmail || inviteMethodInfo.address;

            if (emailValue) {
                inviteForm.fields[this.inviteForm.fieldNames.address].setValue(emailValue);
                this._pendingEmail = '';
            }
        }

        if (this.isCountryCodeEnabled && this.isPhoneNumberEnabled && inviteForm.fields[countryCode]) {
            const countryCodeValue = inviteMethodInfo.countryCode || '';

            countryCodeValue
                ? inviteForm.fields[countryCode].setValue(countryCodeValue)
                : this.initializeAccountCountryCode();
        }

        if (this.isCustomerRefEnabled) {
            inviteForm.createCustomerRefField();
            inviteForm.fields[customerRef].setValue(inviteMethodInfo.customerRef);
        }
        if (this.isEnableAgentWifiScan && this.isEnableNativeFullAssist) {
            inviteForm.createPreDataCollectionCodeRefField();
            inviteForm.fields[preDataCollectionCodeRef].setValue(inviteMethodInfo.preDataCollectionCodeRef);
        }
        if (this.isCoBrowsingEnabled) {
            inviteForm.createCoBrowsingInitialUrlField();
            inviteForm.fields[coBrowsingInitialUrl].setValue(get(this._accountSettings, 'coBrowsing.defaultURL'));
        }

        if (this.isEnableAgentWifiScan && this.isEnableNativeFullAssist) {
            inviteForm.createInitialModes();
            inviteForm.fields[initialNativeMode].setValue(
                this._flowManager.translate(`INVITE.VIEW.NATIVE_FULL_ASSIST.${this.initialNativeMode}`)
            );
        }

        if (this.isLanguageSelectionEnabled) {
            inviteForm.createLanguageField();
            const clientLanguageToSelect =
                inviteMethodInfo.clientLanguage &&
                this.clientLanguages.find((clientLanguage) => clientLanguage.value === inviteMethodInfo.clientLanguage);

            if (clientLanguageToSelect) {
                inviteForm.fields[selectedLanguage].setValue(clientLanguageToSelect.name);
            }
        }

        if (this.isVisualJourneyEnabled && this.visualJourniesList.length) {
            inviteForm.createVisualJourneyField();

            const visualJourneyToSelect = this.visualJourniesList[0].name;

            this.inviteForm.fields[this.inviteForm.fieldNames.visualJourney].setValue(visualJourneyToSelect);
        }

        if (this.isVideoApplicationEnabled || this.isEnableAgentWifiScan) {
            inviteForm.createReferralRegionField();

            const referralConfiguration = find(this.referralRegionList, {
                value: this._accountSettings.referralConfiguration
            });

            this.inviteForm.fields[this.inviteForm.fieldNames.referralRegion].setValue(referralConfiguration!.name);
        }

        if (this.isVirtualBackgroundEnabled && this.virtualBackgroundList.length) {
            inviteForm.createVirtualBackgroundField();

            const background = this.virtualBackgroundList.find((item) => item.value === this.selectedVirtualBackground);

            this.inviteForm.fields[this.inviteForm.fieldNames.virtualBackground].setValue(
                background?.name || this._flowManager.translate('REACT.INVITE.VIEW.SELECT_OPTION_NONE')
            );
        }

        if (this.isSessionTagsEnabled) {
            // Restore any previously chosen tags, dropping ones no longer offered by the account.
            this._selectedSessionTags = (inviteMethodInfo.sessionTags || []).filter(
                (tag) => this.availableSessionTags.indexOf(tag) > -1
            );
        }
    }

    private initializeAccountCountryCode(countryCodeValue?: string | undefined) {
        const {countryCode} = this.inviteForm.fieldNames;
        const countryCodeNumber = countryCodeValue || this._accountSettings.defaultCountryCode;
        const accountDialCode = `+${countryCodeNumber}`;
        const country: ICountryInfo | undefined = find(this.countryList, {dial: accountDialCode});

        if (country && country.dial) {
            this.inviteForm.fields[countryCode].setValue(country.dial);
        }
    }

    private setAddressDetails() {
        const phoneNumber = this.isPhoneNumberEnabled ? this.inviteForm.fields.phoneNumber.value.toString() : '';

        const countryCode = this.isCountryCodeEnabled ? this.inviteForm.fields.countryCode.value.toString() : '';

        const address = calculatePhoneNumberValue(
            phoneNumber,
            countryCode,
            this.isPhoneNumberEnabled,
            this.isCountryCodeEnabled
        );

        this.inviteForm.fields[this.inviteForm.fieldNames.address].setValue(address);
        this._browserUtilsService.saveToLocalStorage('phoneNumber', phoneNumber);
        this._browserUtilsService.saveToLocalStorage('countryCode', countryCode);

        return {address: address, countryCode: countryCode, phoneNumber: phoneNumber};
    }
}
